/**
 * O cliente do serviço `relatorios` — as oito abas de Relatórios (11.10, 11.11) e a Meta (11.9).
 *
 *     GET /resultado-do-mes    aba Resultado do mês
 *     GET /placar              aba Placar (os sete rankings do FR-58)
 *     GET /previsao            aba Previsão
 *     GET /saude-do-funil      aba Saúde do funil
 *     GET /prospeccao          aba Prospecção (RA/RR, conversões, ranking com a SDR)
 *     GET /execucao            aba Execução (atividades, vencidas, por tipo, ranking)
 *     GET /carteira            aba Carteira (Ouro/Prata/Bronze, esquecidas pelo vermelho)
 *     GET /analises            aba Análises (origem, produtos vendidos, perdas, segmentos, regiões)
 *     GET /drill-down          a lista que um número abre (`?indicador=` obrigatório)
 *     GET /metas/{mes}         as metas do mês (a aba Metas da Configuração)
 *     PUT /metas/{mes}         define o mês inteiro (a única escrita deste serviço)
 *
 * O módulo espelha o serviço. **Nenhum tipo de contrato é digitado aqui** (AD-16): todos são
 * apelidos dos gerados em `src/api/tipos-gerados/relatorios.ts`. As rotas do Épico 7 saíram todas
 * (a última, a Carteira esquecida da 7.3, virou a aba Carteira na 11.11).
 *
 * **Nenhuma tela transacional pode chamar este serviço** — a varredura de `src/api/cliente.test.ts`
 * reprova `chamar('relatorios', …)` sob os prefixos de trabalho. Um relatório pode atrasar segundos
 * em relação ao dado vivo (AD-14), e em troca nenhuma tela de trabalho lê daqui.
 *
 * **O período é do serviço**: o cliente manda o **nome** do atalho ou o intervalo, e lê de volta o
 * período aplicado. **Cada aba declara os filtros que aceita** (`filtros.ts`), e só esses viajam.
 * **Nada é calculado aqui** (AD-29): o SPA desenha o que o serviço entrega.
 *
 * `retry: false` e `placeholderData: keepPreviousData` nas leituras: a recusa esperada (`401`
 * renovado pelo cliente, `403` de quem não entra) não muda por insistir, e trocar filtro não pode
 * apagar a tela enquanto a resposta nova não chega.
 */

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { chamar } from '@/api/cliente'
import type { components } from '@/api/tipos-gerados/relatorios'

type Esquemas = components['schemas']

/** Os cinco atalhos do FR-54, como o serviço os publica. */
export type AtalhoDePeriodo = Esquemas['AtalhoDePeriodo']

/** O período **aplicado**, em datas locais (`YYYY-MM-DD`) — o que a tela exibe. */
export type PeriodoAplicado = Esquemas['RespostaDoPeriodo']

export type Atingimento = Esquemas['RespostaDoAtingimento']
export type ResultadoDoMes = Esquemas['RespostaDoResultadoDoMes']
export type VendedorNoResultado = Esquemas['RespostaDoVendedorNoResultado']
export type Placar = Esquemas['RespostaDoPlacar']
export type Previsao = Esquemas['RespostaDaPrevisao']
export type SomaEmNegociacao = Esquemas['RespostaDaSomaEmNegociacao']
export type SaudeDoFunil = Esquemas['RespostaDaSaudeDoFunil']
export type IndicadoresDaSaude = Esquemas['RespostaDosIndicadoresDaSaude']
export type IndicadorDoDrillDown = Esquemas['IndicadorDoDrillDown']
export type ListaDoNumero = Esquemas['RespostaDoDrillDown']
export type NegocioNaLista = Esquemas['RespostaDoNegocioNaLista']
export type AtividadeNaLista = Esquemas['RespostaDaAtividadeNaLista']
export type EmpresaNaLista = Esquemas['RespostaDaEmpresaNaLista']
export type DimensaoDaLinha = Esquemas['DimensaoDaLinha']
export type Prospeccao = Esquemas['RespostaDaProspeccao']
export type Execucao = Esquemas['RespostaDaExecucao']
export type Carteira = Esquemas['RespostaDaCarteira']
export type IndicadoresDaCarteira = Esquemas['RespostaDosIndicadoresDaCarteira']
export type Analises = Esquemas['RespostaDasAnalises']
export type LinhaDaDimensao = Esquemas['RespostaDaLinhaDaDimensao']

/** O recorte de período: um atalho **ou** um intervalo — nunca os dois (o serviço recusa). */
export type Recorte =
  | { readonly tipo: 'atalho'; readonly atalho: AtalhoDePeriodo }
  | { readonly tipo: 'intervalo'; readonly inicio: string; readonly fim: string }

/** Os parâmetros do recorte — `null` é "o padrão da aba", e nada viaja. */
export function parametrosDoRecorte(recorte: Recorte | null): URLSearchParams {
  if (recorte === null) return new URLSearchParams()
  return new URLSearchParams(
    recorte.tipo === 'atalho'
      ? { atalho: recorte.atalho }
      : { inicio: recorte.inicio, fim: recorte.fim },
  )
}

/** Prefixo de toda chave de cache deste serviço. */
export const CHAVE_DOS_RELATORIOS = ['relatorios'] as const

/** As oito rotas de aba, na ordem da lista da Gabi. */
export const CAMINHOS_DAS_ABAS = {
  resultado: '/resultado-do-mes',
  placar: '/placar',
  previsao: '/previsao',
  saude: '/saude-do-funil',
  prospeccao: '/prospeccao',
  execucao: '/execucao',
  carteira: '/carteira',
  analises: '/analises',
} as const

export type AbaComDados = keyof typeof CAMINHOS_DAS_ABAS

interface CorpoPorAba {
  resultado: ResultadoDoMes
  placar: Placar
  previsao: Previsao
  saude: SaudeDoFunil
  prospeccao: Prospeccao
  execucao: Execucao
  carteira: Carteira
  analises: Analises
}

/** A chave de cache de uma aba: o caminho e a query exata — trocar filtro é consulta nova. */
export const chaveDaAba = (aba: AbaComDados, consulta: string) =>
  [...CHAVE_DOS_RELATORIOS, 'aba', aba, consulta] as const

export function lerAba<A extends AbaComDados>(aba: A, consulta: string): Promise<CorpoPorAba[A]> {
  const sufixo = consulta === '' ? '' : `?${consulta}`
  return chamar<CorpoPorAba[A]>('relatorios', `${CAMINHOS_DAS_ABAS[aba]}${sufixo}`)
}

/** A leitura de uma aba, com a query montada pela declaração de filtros dela (`filtros.ts`). */
export function useAba<A extends AbaComDados>(aba: A, consulta: string, habilitada = true) {
  return useQuery({
    queryKey: chaveDaAba(aba, consulta),
    queryFn: () => lerAba(aba, consulta),
    retry: false,
    placeholderData: keepPreviousData,
    enabled: habilitada,
  })
}

export const chaveDoDrillDown = (consulta: string) =>
  [...CHAVE_DOS_RELATORIOS, 'drill-down', consulta] as const

export function lerDrillDown(consulta: string): Promise<ListaDoNumero> {
  return chamar<ListaDoNumero>('relatorios', `/drill-down?${consulta}`)
}

/** A lista que um número abre — só quando há número clicado (`consulta` não nula). */
export function useDrillDown(consulta: string | null) {
  return useQuery({
    queryKey: chaveDoDrillDown(consulta ?? ''),
    queryFn: () => lerDrillDown(consulta ?? ''),
    retry: false,
    enabled: consulta !== null,
  })
}

/* --- A Meta (história 11.9 — FR-77, AD-27) ------------------------------------------------ */

/**
 * O mês lido: a meta do mês, a da equipe, as individuais e — só para a gestão — a soma.
 *
 * `soma_das_individuais` é **opcional no tipo** porque é opcional no corpo: o escopo `propria` não
 * recebe a chave (AD-27). É **referência**, e nunca a meta da equipe: os três números são digitados
 * e independentes (FR-77).
 */
export type MetasDoMes = Esquemas['RespostaDasMetas']

/** Uma linha individual: o vendedor (nome da réplica), a meta ou "sem meta". */
export type MetaIndividual = Esquemas['RespostaDaMetaIndividual']

/** O corpo do `PUT`: o mês inteiro — a individual omitida é apagada (declarado no serviço). */
export type CorpoDasMetas = Esquemas['CorpoDasMetas']

/** A chave de um mês — `YYYY-MM-01`, o primeiro dia (AD-27). */
export const chaveDasMetas = (mes: string) => [...CHAVE_DOS_RELATORIOS, 'metas', mes] as const

/**
 * Lê as metas de um mês (`200`, inclusive o mês sem meta — "sem meta" é estado, e não `404`).
 *
 * **Quem recorta a individual do colega é a policy do serviço**, e não esta tela: o Vendedor
 * recebe só a própria, e a gestão recebe todas mais os vendedores sem meta, marcados.
 */
export function lerMetas(mes: string): Promise<MetasDoMes> {
  return chamar<MetasDoMes>('relatorios', `/metas/${mes}`)
}

/** Define (ou redefine) o mês inteiro, e devolve o mês como ficou. */
export function definirMetas(mes: string, corpo: CorpoDasMetas): Promise<MetasDoMes> {
  return chamar<MetasDoMes>('relatorios', `/metas/${mes}`, { method: 'PUT', corpo })
}

/** As metas daquele mês. */
export function useMetas(mes: string) {
  return useQuery({
    queryKey: chaveDasMetas(mes),
    queryFn: () => lerMetas(mes),
    retry: false,
  })
}

/** O que uma gravação leva: o mês **em que foi disparada** e o corpo. */
export interface GravacaoDasMetas {
  /** `YYYY-MM-01`. */
  mes: string
  corpo: CorpoDasMetas
}

/**
 * Grava o mês e põe a resposta no cache **do mês em que a gravação foi disparada** — a resposta
 * nunca entra no cache de outro mês, mesmo que a tela tenha mudado de mês enquanto ela voava. A
 * resposta **é** a leitura do mês. Os relatórios (11.10) leem a meta, então o resto do prefixo do
 * módulo é invalidado (sem esperar: a tela do mês não depende disso).
 */
export function useDefinirMetas() {
  const clienteDeConsultas = useQueryClient()
  return useMutation({
    mutationFn: ({ mes, corpo }: GravacaoDasMetas) => definirMetas(mes, corpo),
    onSuccess: (metas, { mes }) => {
      clienteDeConsultas.setQueryData(chaveDasMetas(mes), metas)
      void clienteDeConsultas.invalidateQueries({
        queryKey: CHAVE_DOS_RELATORIOS,
        predicate: (consulta) => consulta.queryKey[1] !== 'metas',
      })
    },
  })
}
